import React, { Fragment, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import compose from 'lodash.flowright';
import { connect } from 'react-redux';

import useGlobalWaldogramCheckout from './useGlobalWaldogramCheckout';
import Alert from '../../../../../../components/global/Alert';
import RecipientsReviewModal from './components/RecipientsReviewModal/RecipientsReviewModal';
import { openAlert as openAlertAction } from '../../../../../../actions/modal-actions';
import {
  getGlobalWaldogramSuccessCopy,
  getFinalRecipientsFromAccountCard,
} from './helpers';
import AccountCardConfigurator from '../../../../../../services/cards/views/AccountCardConfigurator';
import { CARD_ELEMENT_TYPE } from '../../../../../../services/cards/constants';
import { accountCardConfigurationValidate } from '../../../../../../services/cards/views/AccountCardConfigurator/helpers';
import PostcardIcon from '../../../../../../components/icons/PostcardIcon';
import DismissButton from '../../../../../../components/global/DismissButton';
import useAccountCardCartCheckout from '../../../../../../services/checkout/hooks/useAccountCardCartCheckout';

const propTypes = {
  externalShippingAddresses: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }).isRequired,
  ),
  rosterIdentityLinkedIdentities: PropTypes.arrayOf(
    PropTypes.shape({
      identityId: PropTypes.string.isRequired,
      rosterIdentityId: PropTypes.string.isRequired,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      fullName: PropTypes.string,
      address: PropTypes.shape({
        id: PropTypes.string.isRequired,
      }),
    }).isRequired,
  ),
  openAlert: PropTypes.func.isRequired,
  onDismiss: PropTypes.func.isRequired,
  showTierSelectionOnTemplateSelect: PropTypes.bool,
  onShowTierSelection: PropTypes.func,
  shouldOpenReview: PropTypes.bool,
  onReviewModalOpenChange: PropTypes.func,
  disableCart: PropTypes.bool,
  enableHolidayCategories: PropTypes.bool,
  photoId: PropTypes.string,
};

const CREATE_STEPS = {
  CREATE: 'CREATE',
  EDIT: 'EDIT',
  REVIEW: 'REVIEW',
};

const GlobalWaldogramConfigurator = ({
  rosterIdentityLinkedIdentities = [],
  externalShippingAddresses = [],
  openAlert,
  onDismiss,
  showTierSelectionOnTemplateSelect,
  onShowTierSelection,
  shouldOpenReview,
  onReviewModalOpenChange,
  disableCart,
  enableHolidayCategories,
  photoId,
}) => {
  const [accountCard, setAccountCard] = useState(null);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [errors, setErrors] = useState([]);
  const [step, setStep] = useState(CREATE_STEPS.CREATE);

  useEffect(() => {
    if (shouldOpenReview && !reviewModalOpen) {
      setReviewModalOpen(true);
      if (onReviewModalOpenChange) {
        onReviewModalOpenChange(false);
      }
    }
  }, [shouldOpenReview, reviewModalOpen, onReviewModalOpenChange]);

  const recipients = getFinalRecipientsFromAccountCard(accountCard);

  const {
    setAccountCard: setCartAccountCard,
    setGlobalAccountCard,
    onAddToCart,
    hasCartItems,
  } = useAccountCardCartCheckout();

  const { checkingOut, checkoutErrors, onCheckout, checkoutPrice } =
    useGlobalWaldogramCheckout({
      accountCard,
      onSuccess: () => {
        openAlert({
          title: 'Wowzers!',
          message: `That was a nice Card! It's on its way to ${getGlobalWaldogramSuccessCopy(
            recipients,
          )} as we speak!`,
          type: 'success',
          onDismiss: () => {
            onDismiss?.();
          },
        });
      },
    });

  const onCardSend = stripeToken => {
    const isConfigurationValid = accountCardConfigurationValidate(accountCard);

    if (accountCard && !isConfigurationValid) {
      setErrors(['Please add a photo, a note, and at least one recipient.']);
      return;
    }

    onCheckout(stripeToken);
  };

  const isValidCard = accountCardConfigurationValidate(accountCard);
  return (
    <Fragment>
      <div className="global-wg-configurator-wrap">
        {step === CREATE_STEPS.CREATE && (
          <Fragment>
            <div className="d-flex justify-content-between align-items-center mb-2 mb-md-0">
              <h2 className="d-flex align-items-center mb-0">
                <PostcardIcon
                  id="global-waldogram-config-post-card-icon"
                  className="mr-2"
                  width={32}
                  color="#000"
                />{' '}
                Send a Waldogram
              </h2>
              {onDismiss && <DismissButton onClick={onDismiss} />}
            </div>
            <hr className="w-100 my-3 d-none d-md-block" />
            <p className="d-none d-md-block">
              Pick an occasion, then choose your favorite design and personalize
              with your own photo, message, and stamp to make the most beautiful
              card!
            </p>
            <p className="d-block d-md-none">
              Pick an occasion, then choose your favorite design!
            </p>
          </Fragment>
        )}
        {step === CREATE_STEPS.EDIT && (
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h2 className="d-flex align-items-center mb-0">
              <PostcardIcon
                id="global-waldogram-config-post-card-icon"
                className="mr-2"
                width={32}
                color="#000"
              />{' '}
              Edit your card
            </h2>
            {onDismiss && <DismissButton onClick={onDismiss} />}
          </div>
        )}
        {step === CREATE_STEPS.REVIEW && (
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h2 className="d-flex align-items-center mb-0">
              <PostcardIcon
                id="global-waldogram-config-post-card-icon"
                className="mr-2"
                width={32}
                color="#000"
              />{' '}
              Review and send
            </h2>
            {onDismiss && <DismissButton onClick={onDismiss} />}
          </div>
        )}
        <div className="global-wg-configurator">
          <AccountCardConfigurator
            onAccountCardChanged={({
              accountCard: changedCard,
              globalAccountCard,
            }) => {
              setAccountCard(changedCard);
              if (!changedCard) {
                setStep(CREATE_STEPS.CREATE);
              } else if (accountCard && step === CREATE_STEPS.CREATE) {
                setStep(CREATE_STEPS.EDIT);
              }
              setAccountCard(changedCard);
              setCartAccountCard(changedCard);
              setGlobalAccountCard(globalAccountCard);
            }}
            presetCardElementConfigurationInputs={[
              {
                cardElement: {
                  type: CARD_ELEMENT_TYPE.ADDRESS,
                },
                cardElementConfiguration: {
                  roster_identity_linked_identity_address_ids:
                    rosterIdentityLinkedIdentities
                      .map(({ address }) => address?.id)
                      .filter(Boolean),
                  external_shipping_address_ids: externalShippingAddresses.map(
                    ({ id }) => id,
                  ),
                },
              },
              photoId
                ? {
                    cardElement: {
                      type: CARD_ELEMENT_TYPE.PHOTO,
                    },
                    cardElementConfiguration: {
                      photo_id: photoId,
                    },
                  }
                : undefined,
            ].filter(Boolean)}
            // Only provide photos context for RosterIdentityId recipients.
            photosContext={
              rosterIdentityLinkedIdentities.length > 0
                ? {
                    roster_identity_ids: rosterIdentityLinkedIdentities?.map(
                      recipient => recipient.rosterIdentityId,
                    ),
                  }
                : undefined
            }
            // Only allow sharing on non PRO cards.
            sharingEnabled={!rosterIdentityLinkedIdentities.length}
            title=""
            hideHeader
            allowReviewing={isValidCard}
            reviewProps={{
              checkingOut,
              onCheckout: () => {
                if (showTierSelectionOnTemplateSelect && onShowTierSelection) {
                  onShowTierSelection();
                } else {
                  setReviewModalOpen(true);
                }
              },
            }}
            onReviewModeChanged={reviewing => {
              if (reviewing && step === CREATE_STEPS.EDIT) {
                setStep(CREATE_STEPS.REVIEW);
              } else if (!reviewing && step === CREATE_STEPS.REVIEW) {
                setStep(CREATE_STEPS.EDIT);
              }
            }}
            onAddToCart={disableCart ? undefined : onAddToCart}
            hasCartItems={disableCart ? false : hasCartItems}
            enableHolidayCategories={enableHolidayCategories}
          />

          {[...checkoutErrors, ...errors].length > 0 && (
            <Alert type="danger" className="mt-2">
              <p className="text-extra-bold mb-1">
                The following errors were found:
              </p>
              <ul className="m-0 pr-1">
                {[...checkoutErrors, ...errors].map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </Alert>
          )}
        </div>
      </div>

      <RecipientsReviewModal
        forceOpen={reviewModalOpen}
        recipients={recipients}
        onConfirm={stripeToken => {
          setReviewModalOpen(false);
          onCardSend(stripeToken);
        }}
        onDismiss={() => {
          setReviewModalOpen(false);
        }}
        price={checkoutPrice}
      />
    </Fragment>
  );
};

GlobalWaldogramConfigurator.propTypes = propTypes;

const mapDispatchToProps = {
  openAlert: openAlertAction,
};

export { GlobalWaldogramConfigurator as GlobalWaldogramConfiguratorComponent };

export default compose(connect(null, mapDispatchToProps))(
  GlobalWaldogramConfigurator,
);
